// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase GST Input Tax Credit register LIST page
// (Stage 6P.6).
//
// Server-paginated, read-only view at
//   /erp/purchase-accounting/gst-itc
//
// The backend is the SOLE authority for every number on this page —
// taxable amount, input CGST/SGST/IGST, total ITC, ITC status, return
// period, gst rate. This page only reads
// (GET /api/erp/purchase-accounting/gst-itc) via
// `purchaseAccountingApi.listGstItc` and shapes the response for the
// AntD grid. There are no row actions, no mutations and no inline
// edits — the ITC register is append-only at the service layer
// (`purchaseAccounting.service` inserts rows when a posting completes;
// future reversal sub-stages own the UPDATE path).
//
// Columns (brief):
//   Bill No · Supplier Invoice No · Supplier GSTIN · Supplier
//   Invoice Date · GST Rate · Taxable Amount · Input CGST ·
//   Input SGST · Input IGST · Total ITC · ITC Status · Return Period.
//
// Toolbar:
//   Search (q over bill no / supplier invoice no / supplier name /
//   supplier gstin — frontend forwards under `search`; the
//   listPurchaseGstItcRegister service ILIKEs the first three
//   server-side, and supplier GSTIN matches as a separate exact
//   filter when the operator types a full 15-char GSTIN).
//   ITC status · Return period · Date range · Clear · Refresh.
//
// Pagination: 25 / 50 / 100 (offset + limit translated from
// page + pageSize because the backend exposes offset/limit, not
// page/pageSize, on purchaseGstItc.service.listPurchaseGstItcRegister).
//
// ADDITIVE-ONLY: this page reads `purchaseAccountingApi.listGstItc`
// + the shared ITC helpers (`ITC_STATUSES`, `itcStatusLabel`,
// `itcStatusColor`, `normalizeGstItcRow`) from
// `./purchaseAccountingHelpers`. It does NOT mutate any row.
//
// Mirrors the compact enterprise AntD list pattern from
// StockLedgerPage / CurrentStockPage:
//   • slate-50 header strip + uppercase column titles
//   • 22 px row density (reuses the `.grn-*` CSS scoped under a
//     `.grn-shell` wrapper class).
//   • sticky toolbar
//   • read-only — no ⋯ row-actions menu for ITC rows in 6P.6.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Input, Select, Space,
  Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, FileDoneOutlined
} from '@ant-design/icons';

import { purchaseAccountingApi } from './purchaseAccountingApi';
import {
  ITC_STATUSES,
  itcStatusLabel,
  itcStatusColor,
  normalizeGstItcRow
} from './purchaseAccountingHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

// 15-char GSTIN regex — used to decide whether to forward the search
// box value as `supplier_gstin` (exact match) in addition to `search`
// (ILIKE on supplier_name / invoice_no / bill_no).
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;

const ITC_STATUS_OPTIONS = ITC_STATUSES.map((v) => ({
  value: v,
  label: itcStatusLabel(v)
}));

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// Money formatter — en-IN grouping, two fraction digits. Falsy /
// non-finite inputs collapse to an em-dash so empty rows don't
// read as "0.00".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

// GST rate display — strip trailing zeros, suffix "%". Treats null /
// 0 as em-dash because a true zero-rate would post nothing into ITC
// and would not be on this register at all.
function fmtPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  return `${s}%`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseGstItcPage() {
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.gst_itc.view` covers admin/manager/staff/viewer per
  // the Stage 6P.6 permissions registry (see PERMS.GST_ITC_VIEW in
  // backend/src/services/erp/purchaseAccounting/purchaseAccounting.constants.js).
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView = hasPerm('erp.purchase.gst_itc.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [itcStatus,     setItcStatus]     = useState(null);
  const [returnPeriod,  setReturnPeriod]  = useState(null);
  const [dateRange,     setDateRange]     = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // Derive a "return period" filter dropdown from the rows the
  // backend has already returned — the backend doesn't yet expose a
  // distinct-periods endpoint, so we surface what we can see. The
  // operator can also type a YYYY-MM value directly in the dropdown
  // because we run it in `showSearch` mode with `tagRender` off.
  const returnPeriodOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      const v = pickField(r, 'return_period', 'returnPeriod');
      if (!v) continue;
      const key = String(v).trim();
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, { value: key, label: key });
    }
    return Array.from(seen.values()).sort(
      (a, b) => String(b.label).localeCompare(String(a.label)) // newest first
    );
  }, [rows]);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the bills / ledger list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      // Backend uses offset/limit, not page/pageSize. Translate.
      const limit  = pageSize;
      const offset = (page - 1) * pageSize;
      const params = { limit, offset };

      const needle = (search || '').trim();
      if (needle) {
        params.search = needle;
        // When the operator pastes a full 15-char GSTIN, send it as
        // an exact-match filter too so the server narrows down hard
        // even if the ILIKE on supplier_name happens to be noisy.
        const upper = needle.toUpperCase();
        if (GSTIN_RE.test(upper)) {
          params.supplier_gstin = upper;
        }
      }
      if (itcStatus)    params.itc_status    = itcStatus;
      if (returnPeriod) params.return_period = returnPeriod;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.bill_date_from = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.bill_date_to   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseAccountingApi.listGstItc(params);
      const rawList = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? rawList.length
        : Number(out?.total ?? out?.count ?? rawList.length) || 0;

      // Normalize every row up front so the column renderers can
      // read a single canonical camelCase shape.
      const normalized = rawList
        .map((r) => normalizeGstItcRow(r))
        .filter(Boolean);

      setRows(normalized);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load GST ITC register.'),
        key: 'pa-gst-itc'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, itcStatus, returnPeriod, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onItcStatus    = (v) => { setItcStatus(v || null);    setPage(1); };
  const onReturnPeriod = (v) => { setReturnPeriod(v || null); setPage(1); };
  const onDates        = (v) => { setDateRange(v || null);    setPage(1); };
  const onReset        = () => {
    setSearchInput('');
    setSearch('');
    setItcStatus(null);
    setReturnPeriod(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      width:     170,
      fixed:     'left',
      render: (_v, row) => {
        const no = pickField(row, 'purchase_bill_no', 'purchaseBillNo');
        if (!no) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{no}</span>;
      }
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     180,
      render: (_v, row) => {
        const no = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!no) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={no}>
            <span className="grn-mono">{no}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Supplier GSTIN',
      dataIndex: 'supplierGstin',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_gstin', 'supplierGstin');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{String(v).toUpperCase()}</span>;
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name = pickField(row, 'supplier_name', 'supplierName') || '—';
        return (
          <Tooltip title={name}>
            <span className="grn-supplier__name">{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Invoice Date',
      dataIndex: 'supplierInvoiceDate',
      width:     130,
      render: (_v, row) => {
        const d = pickField(row, 'supplier_invoice_date', 'supplierInvoiceDate')
               || pickField(row, 'bill_date',             'billDate');
        return <span className="grn-mono">{fmtDate(d)}</span>;
      }
    },
    {
      title:     'GST Rate',
      dataIndex: 'gstRate',
      width:     90,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtPercent(pickField(row, 'gst_rate', 'gstRate'))}
        </span>
      )
    },
    {
      title:     'Taxable Amount',
      dataIndex: 'taxableAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMoney(pickField(row, 'taxable_amount', 'taxableAmount'))}
        </span>
      )
    },
    {
      title:     'Input CGST',
      dataIndex: 'inputCgst',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMoney(
            pickField(row, 'input_cgst', 'inputCgst')
              ?? pickField(row, 'cgst_amount', 'cgstAmount')
          )}
        </span>
      )
    },
    {
      title:     'Input SGST',
      dataIndex: 'inputSgst',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMoney(
            pickField(row, 'input_sgst', 'inputSgst')
              ?? pickField(row, 'sgst_amount', 'sgstAmount')
          )}
        </span>
      )
    },
    {
      title:     'Input IGST',
      dataIndex: 'inputIgst',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtMoney(
            pickField(row, 'input_igst', 'inputIgst')
              ?? pickField(row, 'igst_amount', 'igstAmount')
          )}
        </span>
      )
    },
    {
      title:     'Total ITC',
      dataIndex: 'totalItc',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(
            pickField(row, 'total_itc',        'totalItc')
              ?? pickField(row, 'total_tax_amount', 'totalTaxAmount')
          )}
        </span>
      )
    },
    {
      title:     'ITC Status',
      dataIndex: 'itcStatus',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'itc_status', 'itcStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={itcStatusColor(v)} className="grn-status-tag">
            {itcStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Return Period',
      dataIndex: 'returnPeriod',
      width:     120,
      render: (_v, row) => {
        const v = pickField(row, 'return_period', 'returnPeriod');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    }
  ]), []);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view the GST ITC register."
          description="Ask your master account to grant erp.purchase.gst_itc.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><FileDoneOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">GST Input Tax Credit Register</span>
            <span className="grn-headstrip__subtitle">
              Append-only register of every GST-bearing purchase bill
              line — one row per line with non-zero CGST / SGST / IGST.
              Backend remains the authority for taxable amount, ITC
              eligibility and return-period bucketing; this view is
              read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search bill no, invoice no, supplier, GSTIN…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All ITC statuses"
          value={itcStatus || undefined}
          onChange={onItcStatus}
          options={ITC_STATUS_OPTIONS}
          style={{ width: 170 }}
        />
        <Select
          allowClear
          showSearch
          size="small"
          placeholder="Return period (YYYY-MM)"
          value={returnPeriod || undefined}
          onChange={onReturnPeriod}
          options={returnPeriodOptions}
          optionFilterProp="label"
          style={{ width: 200 }}
          notFoundContent={
            returnPeriodOptions.length === 0
              ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                  No return periods on screen
                </span>
              : null
          }
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['Bill from', 'Bill to']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id') ||
              `${pickField(r, 'purchase_bill_id',      'purchaseBillId')      || ''}:` +
              `${pickField(r, 'purchase_bill_line_id', 'purchaseBillLineId') || ''}`
            }
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No ITC rows yet — post a GST-bearing purchase bill to accounts to populate the register.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
